/** MatterFormModal — form modal for creating or editing a matter. */

import { useEffect, useState } from "react";
import { Modal, Input, Select, Form, message } from "antd";
import { useCreateMatter, useUpdateMatter, useClients, useUsers } from "../../api/queries";
import { MATTER_TYPE_LABELS } from "../../constants";
import type { Client, Matter, MatterType } from "../../types";
import SelectCreateFooter from "../common/SelectCreateFooter";
import ClientFormModal from "../client/ClientFormModal";
import styles from "../../styles/matter.module.css";

const MATTER_TYPE_OPTIONS = Object.entries(MATTER_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

interface MatterFormValues {
  name: string;
  clientId: string;
  type: MatterType;
  leadLawyerId: string;
  description: string;
}

const INITIAL_VALUES: MatterFormValues = {
  name: "",
  clientId: "",
  type: "litigation" as MatterType,
  leadLawyerId: "",
  description: "",
};

interface MatterFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Matter to edit - if provided, modal is in edit mode */
  matter?: Matter;
  /** 新建模式下预填的事项类型（「事项与客户」三栏页的事项模板选择后带入）。 */
  defaultType?: MatterType;
  /** 新建成功后回调，携带刚创建的事项。编辑模式不触发。 */
  onCreated?: (created: Matter) => void;
}

export default function MatterFormModal({
  open,
  onClose,
  matter,
  defaultType,
  onCreated,
}: MatterFormModalProps) {
  const isEdit = !!matter;
  const [form] = Form.useForm<MatterFormValues>();
  const createMatter = useCreateMatter();
  const updateMatter = useUpdateMatter();
  const [messageApi, contextHolder] = message.useMessage();
  // 「所属客户」是必填项，而首次使用时客户列表为空。允许在本弹窗内直接建客户，
  // 否则律师必须放弃当前事项表单、去客户页建好再回来重填。
  const [clientModalOpen, setClientModalOpen] = useState(false);
  // 刚建好的客户先并进选项，不等列表重新拉取——否则这段时间下拉里没有该项，
  // Select 会把选中值原样显示成一串 id。
  const [justCreatedClient, setJustCreatedClient] = useState<Client | null>(null);

  // Fetch clients for the client select dropdown
  const { data: clientsData } = useClients({ page: 1, pageSize: 100 });
  const { data: usersData } = useUsers({ pageSize: 100 });

  const fetchedClients = clientsData?.data ?? [];
  const clientOptions = (
    justCreatedClient &&
    !fetchedClients.some((c) => c.id === justCreatedClient.id)
      ? [justCreatedClient, ...fetchedClients]
      : fetchedClients
  ).map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const lawyerOptions =
    (usersData?.data ?? []).map((u) => ({
      value: u.id,
      label: u.name,
    }));

  // Prefill form when editing
  useEffect(() => {
    if (matter) {
      form.setFieldsValue({
        name: matter.name,
        clientId: matter.clientId,
        type: matter.type,
        leadLawyerId: matter.leadLawyerId ?? "",
        description: matter.description ?? "",
      });
    } else {
      form.setFieldsValue({
        ...INITIAL_VALUES,
        ...(defaultType ? { type: defaultType } : {}),
      });
    }
  }, [matter, open, form, defaultType]);

  const handleSubmit = async () => {
    let values: MatterFormValues;
    try {
      values = await form.validateFields();
    } catch {
      // Validation failed — Ant Design shows inline errors automatically
      return;
    }

    try {
      if (isEdit && matter) {
        await updateMatter.mutateAsync({
          id: matter.id,
          data: {
            name: values.name.trim(),
            clientId: values.clientId,
            type: values.type,
            leadLawyerId: values.leadLawyerId || null,
            description: values.description.trim() || null,
          },
        });
        messageApi.success("事项更新成功");
      } else {
        const res = await createMatter.mutateAsync({
          name: values.name.trim(),
          clientId: values.clientId,
          type: values.type,
          leadLawyerId: values.leadLawyerId || undefined,
          description: values.description.trim() || null,
        });
        messageApi.success("事项创建成功");
        if (res?.data) onCreated?.(res.data);
      }
      form.resetFields();
      onClose();
    } catch {
      messageApi.error(isEdit ? "更新失败，请稍后重试" : "创建失败，请稍后重试");
    }
  };

  const handleClose = () => {
    if (!createMatter.isPending && !updateMatter.isPending) {
      form.resetFields();
      onClose();
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={isEdit ? "编辑事项" : "新建事项"}
        open={open}
        onCancel={handleClose}
        onOk={handleSubmit}
        okText="确认"
        cancelText="取消"
        confirmLoading={createMatter.isPending || updateMatter.isPending}
        okButtonProps={{ className: "" }}
        cancelButtonProps={{ className: "" }}
        width={560}
        rootClassName="jerryMatterFormModal"
        destroyOnHidden
      >
        <Form form={form} initialValues={INITIAL_VALUES} layout="vertical" requiredMark={false}>
          <div className={styles.formRow}>
            {/* Name — full width */}
            <div className={styles.formGroupFull}>
              <Form.Item
                name="name"
                label={<span className={styles.formLabelRequired}>事项名称</span>}
                rules={[{ required: true, message: "请输入事项名称" }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder="如：张三诉李四合同纠纷"
                  maxLength={100}
                />
              </Form.Item>
            </div>

            {/* Client */}
            <div className={styles.formGroup}>
              <Form.Item
                name="clientId"
                label={<span className={styles.formLabelRequired}>所属客户</span>}
                rules={[{ required: true, message: "请选择所属客户" }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  options={clientOptions}
                  placeholder="选择客户"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                  popupRender={(menu) => (
                    <SelectCreateFooter
                      menu={menu}
                      label="新建客户"
                      onCreate={() => setClientModalOpen(true)}
                      testId="matter-form-create-client"
                    />
                  )}
                  notFoundContent="暂无客户，可在下方新建"
                />
              </Form.Item>
            </div>

            {/* Matter Type */}
            <div className={styles.formGroup}>
              <Form.Item
                name="type"
                label={<span className={styles.formLabel}>事项类型</span>}
                style={{ marginBottom: 0 }}
              >
                <Select
                  options={MATTER_TYPE_OPTIONS}
                />
              </Form.Item>
            </div>

            {/* Lead Lawyer */}
            <div className={styles.formGroup}>
              <Form.Item
                name="leadLawyerId"
                label={<span className={styles.formLabel}>主办律师</span>}
                style={{ marginBottom: 0 }}
              >
                <Select
                  options={lawyerOptions}
                  placeholder="选择主办律师（可选）"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                />
              </Form.Item>
            </div>

            {/* Description — full width */}
            <div className={styles.formGroupFull}>
              <Form.Item
                name="description"
                label={<span className={styles.formLabel}>描述</span>}
                style={{ marginBottom: 0 }}
              >
                <Input.TextArea
                  placeholder="简要描述事项背景和委托范围"
                  rows={3}
                  maxLength={500}
                />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>

      <ClientFormModal
        open={clientModalOpen}
        onClose={() => setClientModalOpen(false)}
        onCreated={(created) => {
          setJustCreatedClient(created);
          form.setFieldsValue({ clientId: created.id });
        }}
      />
    </>
  );
}
